import React, { useState, useMemo } from "react";
import DataTable from "components/DataTable";
import PageWrapper from "components/PageWrapper";
import { useTableData } from "../../hooks/useTableData";
import { dayWiseUserReportConfig } from "config/tableConfigs";
import { useSession } from "hooks/useSession";

// ─────────────────────────────────────────────────────────────────────────────
// Column keys per role — detail rows
// Only opening balance, amount, closing balance — no commission/tds/charge
// because fund_transfer and credit_debit don't earn commission
// ─────────────────────────────────────────────────────────────────────────────
const DETAIL_COLUMNS = {
  Retailer: [
    "id", "txn_date", "txn_time", "bc_name", "bc_phone",
    "service", "txn_flow", "amount", "status",
    "Opening Balance BC", "Closing Balance BC",
    "ref_no", "ack_no", "remark", "created_at",
  ],
  "API Partner": [
    "id", "txn_date", "txn_time", "bc_name", "bc_phone",
    "service", "txn_flow", "amount", "status",
    "Opening Balance BC", "Closing Balance BC",
    "ref_no", "ack_no", "api_txn_id", "created_at",
  ],
  Distributer: [
    "id", "txn_date", "txn_time", "bc_name", "bc_phone",
    "service", "txn_flow", "amount", "status",
    "Opening Balance Dist", "Closing Balance Dist",
    "Opening Balance BC", "Closing Balance BC",
    "ref_no", "ack_no", "created_at",
  ],
  "Master Distributor": [
    "id", "txn_date", "txn_time", "bc_name", "bc_phone",
    "service", "txn_flow", "amount", "status",
    "Opening Balance MD", "Closing Balance MD",
    "Opening Balance Dist", "Closing Balance Dist",
    "Opening Balance BC", "Closing Balance BC",
    "ref_no", "ack_no", "created_at",
  ],
  "White label": [
    "id", "txn_date", "txn_time", "bc_name", "bc_phone",
    "service", "txn_flow", "amount", "status",
    "Opening Balance WL", "Closing Balance WL",
    "Opening Balance MD", "Closing Balance MD",
    "Opening Balance Dist", "Closing Balance Dist",
    "Opening Balance BC", "Closing Balance BC",
    "ref_no", "ack_no", "created_at",
  ],
  Admin: [
    "id", "txn_date", "txn_time", "bc_name", "bc_phone",
    "service", "txn_flow", "amount", "status",
    "Opening Balance Admin", "Closing Balance Admin",
    "Opening Balance WL", "Closing Balance WL",
    "Opening Balance MD", "Closing Balance MD",
    "Opening Balance Dist", "Closing Balance Dist",
    "Opening Balance BC", "Closing Balance BC",
    "ref_no", "ack_no", "created_at",
  ],
  "Main Admin": null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Column keys per role — day summary view
// ─────────────────────────────────────────────────────────────────────────────
const SUMMARY_COLUMNS = {
  Retailer:           ["txn_date", "bc_name", "bc_phone", "service", "total_txn", "total_amount"],
  "API Partner":      ["txn_date", "bc_name", "bc_phone", "service", "total_txn", "total_amount"],
  Distributer:        ["txn_date", "bc_name", "bc_phone", "service", "total_txn", "total_amount"],
  "Master Distributor":["txn_date","bc_name", "bc_phone", "service", "total_txn", "total_amount"],
  "White label":      ["txn_date", "bc_name", "bc_phone", "service", "total_txn", "total_amount"],
  Admin:              ["txn_date", "bc_id", "bc_name", "bc_phone", "service", "total_txn", "total_amount"],
  "Main Admin":       null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Service filter — only the 2 products the API returns
// ─────────────────────────────────────────────────────────────────────────────
const SERVICE_TABS = [
  { key: "all",          label: "All" },
  { key: "fund_transfer",label: "Fund Transfer" },
  { key: "credit_debit", label: "Credit / Debit" },
];

const VIEW_TABS = [
  { key: "detail",  label: "Detail" },
  { key: "summary", label: "Day Summary" },
  { key: "service", label: "By Service" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const getTodayRange = () => {
  const todayStr = new Date().toISOString().split("T")[0];
  return { start: todayStr, end: todayStr };
};

const buildUrl = (userid, start, end, service = "") => {
  let url = `https://api.mokshiq.in/day_wise_user_report?user=${userid}&filter_date=${start}&end_date=${end}`;
  if (service && service !== "all") url += `&service=${service}`;
  return url;
};

const tabStyle = (active) => ({
  padding: "6px 18px",
  borderRadius: "6px",
  border: "none",
  cursor: "pointer",
  fontWeight: active ? "600" : "400",
  background: active ? "#4f46e5" : "#e5e7eb",
  color: active ? "#fff" : "#374151",
  transition: "all 0.15s",
});

const chipStyle = (active) => ({
  padding: "4px 14px",
  borderRadius: "20px",
  border: active ? "1px solid #4f46e5" : "1px solid #d1d5db",
  cursor: "pointer",
  fontWeight: active ? "600" : "400",
  background: active ? "#4f46e5" : "#fff",
  color: active ? "#fff" : "#6b7280",
  fontSize: "12px",
  transition: "all 0.15s",
  whiteSpace: "nowrap",
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const DayWiseUserReportPage = () => {
  const { userid, role } = useSession();
  const { start, end } = getTodayRange();

  const [apiUrl, setApiUrl]               = useState(() => buildUrl(userid, start, end));
  const [activeService, setActiveService] = useState("all");
  const [activeView, setActiveView]       = useState("detail");

  const rawResponse = useTableData(apiUrl, 0, dayWiseUserReportConfig);

  // Pick correct array based on active view
  const viewData = useMemo(() => {
    if (!rawResponse?.data) return rawResponse;

    let rows;
    if (activeView === "summary") {
      rows = rawResponse.day_wise_summary || [];
    } else if (activeView === "service") {
      rows = rawResponse.service_totals || [];
    } else {
      rows = rawResponse.data || [];
    }

    // client-side filter for detail view
    // handle DB typo: fund_trasfer = same as fund_transfer
    if (activeView === "detail" && activeService && activeService !== "all") {
      rows = rows.filter((r) =>
        activeService === "fund_transfer"
          ? r.service === "fund_transfer" || r.service === "fund_trasfer"
          : r.service === activeService
      );
    }

    return { ...rawResponse, data: rows };
  }, [rawResponse, activeView, activeService]);

  // Filter columns by role + view
  const filteredConfig = useMemo(() => {
    const isMainAdmin = DETAIL_COLUMNS[role] === null;

    let keys;
    if (activeView === "summary") {
      keys = isMainAdmin ? null : (SUMMARY_COLUMNS[role] || []);
    } else if (activeView === "service") {
      keys = ["service", "total_txn", "total_amount"];
    } else {
      keys = isMainAdmin ? null : (DETAIL_COLUMNS[role] || []);
    }

    if (keys === null) return dayWiseUserReportConfig;
    if (!keys.length)  return { ...dayWiseUserReportConfig, columns: [] };

    const allowedSet = new Set(keys);
    return {
      ...dayWiseUserReportConfig,
      columns: dayWiseUserReportConfig.columns.filter((col) =>
        allowedSet.has(col.key)
      ),
    };
  }, [role, activeView]);

  // Count rows per product
  const serviceCounts = useMemo(() => {
    const rows = rawResponse?.data || [];
    return {
      all:           rows.length,
      fund_transfer: rows.filter((r) => r.service === "fund_transfer" || r.service === "fund_trasfer").length,
      credit_debit:  rows.filter((r) => r.service === "credit_debit").length,
    };
  }, [rawResponse]);

  const g = rawResponse?.grand_totals || {};

  const handleDateFilter = (s, e) => {
    setApiUrl(buildUrl(userid, s, e, activeService));
  };

  const handleServiceChange = (svc) => {
    setActiveService(svc);
    const { start: s, end: e } = getTodayRange();
    setApiUrl(buildUrl(userid, s, e, svc));
  };

  return (
    <PageWrapper config={dayWiseUserReportConfig} onDateFilter={handleDateFilter}>

      {/* Product filter chips */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        {SERVICE_TABS.map(({ key, label }) => (
          <button
            key={key}
            style={chipStyle(activeService === key)}
            onClick={() => handleServiceChange(key)}
          >
            {label} ({serviceCounts[key] ?? 0})
          </button>
        ))}
      </div>

      {/* View tabs */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
        {VIEW_TABS.map(({ key, label }) => (
          <button
            key={key}
            style={tabStyle(activeView === key)}
            onClick={() => setActiveView(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Grand totals */}
      {rawResponse?.grand_totals && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "10px", marginBottom: "16px" }}>
          {[
            { label: "Total Txns",   value: g.total_txn    ?? 0, isCount: true  },
            { label: "Total Amount", value: g.total_amount ?? 0, isCount: false },
          ].map(({ label, value, isCount }) => (
            <div
              key={label}
              style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px 14px" }}
            >
              <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {label}
              </div>
              <div style={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}>
                {isCount
                  ? Number(value).toLocaleString("en-IN")
                  : "₹" + parseFloat(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <DataTable config={filteredConfig} tableData={viewData} />

    </PageWrapper>
  );
};

export default DayWiseUserReportPage;